import React, { useState, useRef, useEffect } from 'react'
import { Feature } from '../../api/client'

/**
 * Shared chrome for the composer's list editors (regular reference lists,
 * locations, and any future list value type that wants the same UX).
 *
 * Apple-style commit semantics: every mutation (add row, delete row, or
 * row-replace via `onRowClick`) immediately fires `onChange` with the new
 * list. Click-outside, Esc, and the header × all just dismiss — no batched
 * save, no commit. The parent stamps `KEEP_VALUE` on every row of every
 * commit, so a no-op inspection that just opens-and-closes leaves rows
 * untouched.
 *
 * Owns:
 *  - the local `items` array (seeded from `initialItems`, mirrored to parent
 *    on every mutation so the popover stays interactive while parent state
 *    propagates back through props)
 *  - the absolute-positioned card frame, "Edit <feature.title>" header, and
 *    the per-row trash button
 *
 * Delegates to the parent via render props:
 *  - `renderRow(item, index)`            — how to display one row
 *  - `renderAddTrigger(onAdd)`           — the "+ Add" button + child picker
 *  - `onRowClick?(item, index, replace)` — optional per-row click; `replace`
 *                                          swaps the row in place and commits
 *
 * Lists/maps use a feature-level lock: every mutation stamps KEEP_VALUE on
 * the resulting rows; emptying a populated list emits a DELETE-sentinel
 * row (all value columns NULL). The parent's `handle*Change` implements
 * that policy on top of this shell's mutation events.
 */
export interface ItemListEditorShellProps<T> {
    feature: Feature
    initialItems: T[]
    /** Render the row body (left of the trash button). */
    renderRow: (item: T, index: number) => React.ReactNode
    /** Render the "+ Add" trigger. The shell calls `onAdd(newItem)` to
     *  append (and commit). */
    renderAddTrigger: (onAdd: (item: T) => void) => React.ReactNode
    /** Optional: per-row click. Receives a `replace(newItem)` callback that
     *  swaps the row in place and commits. */
    onRowClick?: (item: T, index: number, replace: (newItem: T) => void) => void
    /** Fired on every mutation with the post-mutation list. */
    onChange: (items: T[]) => void
    /** Fired on dismiss (click-outside, Esc, header ×). No commit. */
    onClose: () => void
    /** Optional override for the empty-list placeholder text. */
    emptyText?: string
    /** Optional set-enforcement guard (ENFORCE_AS_SET_PLAN Stage 2). When
     *  supplied, an add or row-replace whose candidate `isDuplicate` of the
     *  rows it would join is rejected (no commit) and the shell flashes a
     *  short inline message instead. `others` excludes the row being
     *  replaced so re-confirming an unchanged row is never a "duplicate". */
    isDuplicate?: (candidate: T, others: T[]) => boolean
    /** Message shown when `isDuplicate` rejects a mutation. */
    duplicateMessage?: string
}

export function ItemListEditorShell<T>({
    feature,
    initialItems,
    renderRow,
    renderAddTrigger,
    onRowClick,
    onChange,
    onClose,
    emptyText = 'No items yet',
    isDuplicate,
    duplicateMessage = 'Already added',
}: ItemListEditorShellProps<T>) {
    const [items, setItems] = useState<T[]>(initialItems)
    const [dupWarning, setDupWarning] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    // Latest `items` snapshot for callbacks held by async pickers (e.g.
    // LocationPickerModal) that may fire after additional mutations.
    const itemsRef = useRef(items)
    useEffect(() => {
        itemsRef.current = items
    }, [items])

    // Transient "Already added" flash for rejected set-enforcement adds.
    const warnTimerRef = useRef<ReturnType<typeof setTimeout>>()
    const flashDuplicate = () => {
        setDupWarning(true)
        if (warnTimerRef.current) clearTimeout(warnTimerRef.current)
        warnTimerRef.current = setTimeout(() => setDupWarning(false), 2500)
    }
    useEffect(
        () => () => {
            if (warnTimerRef.current) clearTimeout(warnTimerRef.current)
        },
        [],
    )

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onClose()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
        }
    }

    const commit = (next: T[]) => {
        setItems(next)
        onChange(next)
    }

    const removeAt = (index: number) => {
        commit(itemsRef.current.filter((_, i) => i !== index))
    }

    const addItem = (item: T) => {
        if (isDuplicate?.(item, itemsRef.current)) {
            flashDuplicate()
            return
        }
        commit([...itemsRef.current, item])
    }

    return (
        <div
            ref={containerRef}
            className="absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 min-w-64 p-3"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-700">
                    Edit {feature.title}
                </div>
                <button
                    onClick={onClose}
                    className="p-1 text-gray-400 hover:text-gray-700 rounded"
                    title="Dismiss"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className="space-y-2 mb-3">
                {items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                        {onRowClick ? (
                            <button
                                type="button"
                                onClick={() =>
                                    onRowClick(item, index, (newItem) => {
                                        const others = itemsRef.current.filter((_, i) => i !== index)
                                        if (isDuplicate?.(newItem, others)) {
                                            flashDuplicate()
                                            return
                                        }
                                        commit(
                                            itemsRef.current.map((it, i) => (i === index ? newItem : it))
                                        )
                                    })
                                }
                                className="flex-1 text-left px-2 py-1 text-sm cursor-pointer hover:bg-gray-50 rounded"
                            >
                                {renderRow(item, index)}
                            </button>
                        ) : (
                            <div className="flex-1 px-2 py-1 text-sm rounded">
                                {renderRow(item, index)}
                            </div>
                        )}

                        <button
                            onClick={() => removeAt(index)}
                            className="p-1 text-gray-400 hover:text-rose-500 rounded"
                            title="Remove item"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                ))}

                {items.length === 0 && (
                    <div className="text-sm text-gray-500 text-center py-2">
                        {emptyText}
                    </div>
                )}
            </div>

            {renderAddTrigger(addItem)}

            {dupWarning && (
                <div className="mt-2 text-xs text-rose-600" role="alert">
                    {duplicateMessage}
                </div>
            )}
        </div>
    )
}
